import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UserInfo {
  id: number;
  username: string;
  name?: string;
  /** 角色三档（W5）：admin 管理员 / supervisor 主管 / agent 坐席 */
  role?: string;
  extension?: string;
  sipPassword?: string;
  agentId?: number;
  /** 技能组（本期仅展示） */
  skill_group_id?: number | null;
  /** 首次登录须强制改密（W5-4）：为 true 时除改密外 API 一律 403 code=4001 */
  must_change_password?: boolean;
  [key: string]: any;
}

/** RBAC 上下文（/auth/me + /auth/menus，前端按后端权限渲染） */
export interface RbacInfo {
  roles: string[];
  permissions: string[];
  menus: AuthMenuItem[];
  orgId: number;
  orgScope: string;
}

/** 后端菜单节点（与 api/auth.ts AuthMenu 同构，供侧栏渲染） */
export interface AuthMenuItem {
  code: string;
  name: string;
  type: string;
  parent_code?: string;
  route?: string;
  sort?: number;
  children?: AuthMenuItem[];
}

/** 全局改密 Modal 状态：forced=首次登录/4001 触发，不可关闭不可跳过 */
export interface PwdModalState {
  visible: boolean;
  forced: boolean;
}

interface AuthState {
  token: string;
  user: UserInfo | null;
  pwdModal: PwdModalState;
  /** RBAC 上下文（后端菜单/权限/组织范围） */
  rbac: RbacInfo | null;
  /**
   * RBAC 引导失败的原因（成功即清空）。判据依赖权限码，拉不到上下文时既不能当「无权限」
   * 也不能一直转圈——由守卫据此显示错误态与重试入口。
   */
  rbacError: string | null;
  setAuth: (token: string, user: UserInfo) => void;
  setUser: (user: UserInfo) => void;
  setRbac: (rbac: RbacInfo) => void;
  setRbacError: (msg: string | null) => void;
  logout: () => void;
  /** 打开改密 Modal；forced=true 时锁定（不可取消），已为 forced 则保持锁定 */
  openPwdModal: (forced?: boolean) => void;
  closePwdModal: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: '',
      user: null,
      pwdModal: { visible: false, forced: false },
      rbac: null,
      rbacError: null,
      setAuth: (token, user) => set({ token, user }),
      setUser: (user) => set({ user }),
      setRbac: (rbac) => set({ rbac, rbacError: null }),
      setRbacError: (msg) => set({ rbacError: msg }),
      logout: () => {
        // 退出登录必须落服务端：JWT 无状态，只清本地等于「凭据在有效期内仍可用」，
        // 本系统自己的接口、以及宿主嵌入系统（WeKnora 直认本系统 token）都会继续放行。
        // 用 keepalive 保证页面跳转/关闭时请求也能发出去；失败不阻断退出（本地态照清）。
        const token = get().token;
        if (token) {
          try {
            void fetch('/api/v1/logout', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              keepalive: true,
            }).catch(() => {});
          } catch {
            /* 网络不可用时忽略：本地态照样清空 */
          }
        }
        set({
          token: '',
          user: null,
          rbac: null,
          rbacError: null,
          pwdModal: { visible: false, forced: false },
        });
      },
      openPwdModal: (forced) =>
        set((s) => ({
          pwdModal: { visible: true, forced: forced || s.pwdModal.forced },
        })),
      closePwdModal: () => set({ pwdModal: { visible: false, forced: false } }),
    }),
    {
      name: 'bcc-auth',
      // 仅持久化登录态；rbac/Modal 等瞬态不落盘（刷新后由侧栏重新拉取）
      partialize: (s) => ({ token: s.token, user: s.user }),
    }
  )
);

export function getToken(): string {
  return useAuthStore.getState().token;
}
